import React, { useEffect, useState } from 'react';
import { Button, Typography, Box, CircularProgress, Grid2, Paper } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { Amenity, APICallStatus, DataTypes } from '../Types/UnitFeatureTypes';
import { useDispatch, useSelector } from 'react-redux';
import { Actions } from '../Redux/Reducer/rootReducer';
import { bookingCreateRequestStatus, bookingHoldId, bookingUpdateRequestStatus } from '../Redux/selectors';
import { useApp } from '../context/appContext';

interface BookingConfirmationProps {
  bookingDate: Date;
  bookingSlots: Date[];
  amenity: Amenity;
  holdId: number;
}

const BookingConfirmation: React.FC = () => {
  const location = useLocation();
  const state = location.state as BookingConfirmationProps;
  const [timer, setTimer] = useState<number>(300);
  const [isBookingHeld, setIsBookingHeld] = useState<boolean>(false);
  const bookingCreateStatus = useSelector(bookingCreateRequestStatus);
  const bookingUpdateStatus = useSelector(bookingUpdateRequestStatus);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {setSnackbar} = useApp();
    
  useEffect(() => {
    if (!state) {
      navigate(-1);
    }
  }, []);

  useEffect(() => {
    const storedTimer = sessionStorage.getItem('bookingTimer');
    const storedIsHeld = sessionStorage.getItem('isBookingHeld');

    if (storedTimer) {
      setTimer(Number(storedTimer));
    }
    if (storedIsHeld) {
      setIsBookingHeld(JSON.parse(storedIsHeld));
    }

    const timeout = setTimeout(() => {
      if (timer > 0) {
      setTimer(prev => {
        const newTimer = prev - 1;
        sessionStorage.setItem('bookingTimer', newTimer.toString());
        return newTimer;
      });
      } else {
      setIsBookingHeld(false);
      sessionStorage.removeItem('isBookingHeld');
      sessionStorage.removeItem('bookingTimer');
      dispatch(Actions.deleteBooking.deleteBookingHoldRequestAction.request({ id: state.holdId }));
      navigate('/createBooking', { replace: true });
      }
    }, 1000);

    return () => clearTimeout(timeout); // Cleanup on unmount
  }, [timer]);

  useEffect(() => {
    if (bookingCreateStatus === APICallStatus.SUCCESS) {
      sessionStorage.removeItem('isBookingHeld');
      sessionStorage.removeItem('bookingTimer');
      navigate('/viewBooking', { replace: true, state: { id: state.holdId, type: DataTypes.BOOKING } });
      setSnackbar(true, 'Booking confirmed successfully', 'success');
      dispatch(Actions.booking.resetStatusRequestAction());
    } else if (bookingCreateStatus === APICallStatus.FAILURE) {
      alert('Booking failed. Please try again.');
      navigate('/createBooking', { replace: true });
      setSnackbar(true, 'Failed confirm booking', 'error');
      dispatch(Actions.booking.resetStatusRequestAction());
    }
  }, [bookingCreateStatus]);

  useEffect(() => {
    if (bookingUpdateStatus === APICallStatus.SUCCESS) {
      sessionStorage.removeItem('isBookingHeld');
      sessionStorage.removeItem('bookingTimer');
      navigate('/viewBooking', { replace: true, state: { id: state.holdId, type: DataTypes.BOOKING } });
      setSnackbar(true, 'Booking updated successfully', 'success');
      dispatch(Actions.booking.resetStatusRequestAction());
    } else if (bookingUpdateStatus === APICallStatus.FAILURE) {
      navigate('/createBooking', { replace: true });
      setSnackbar(true, 'Failed update booking', 'error');
      dispatch(Actions.booking.resetStatusRequestAction());
    }
    }, [bookingUpdateStatus]);

  const handleConfirmBooking = () => {
    if (location?.state && location?.state?.reschedule) {
      dispatch(Actions.booking.updateBookingRequestAction.request({
        bookingData: {
          id: state.holdId,
          slots: state.bookingSlots,
          amenity_id: state.amenity.id ?? -1,
          booking_start: state.bookingDate
        }
      }));
    } else {
      dispatch(Actions.booking.createBookingRequestAction.request({
      id: state.holdId
      }));
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  return (
    <Paper elevation={5} sx={{ padding: 6, borderRadius: 3, mb: 2 }}>
      <Typography variant="h4" gutterBottom>Booking Confirmation</Typography>
      <Typography variant="h6" color="textSecondary">Booking Date: {state?.bookingDate?.toDateString()}</Typography>
      <Typography variant="h6" color="textSecondary">Booked Slots</Typography>
      <Grid2 container spacing={1}>
        {state?.bookingSlots.map((slot, index) =>
          (
            <Grid2 key={index}>
              <Button variant="outlined">
                {slot.toLocaleTimeString('default', { hour: '2-digit', minute: '2-digit', hour12: false })}
              </Button>
            </Grid2>
          )
        )}
      </Grid2>

      <Box sx={{ marginTop: 2 }}>
        <Typography variant="subtitle2" color="textSecondary">
          Please note: You have 5 minutes to complete your booking. After that, the booking will be released.
        </Typography>

        <Typography variant="body1" color="error" sx={{ marginTop: 1 }}>
          Time Remaining: {formatTime(timer)}
        </Typography>
      </Box>

      {state?.amenity?.require_payment && state?.amenity?.price !== undefined && (
        <Box sx={{ marginTop: 2 }}>
          <Typography variant="h6" color="primary">
            Payment Required: ${state?.amenity.price}
          </Typography>
        </Box>
      )}

      {state?.amenity?.require_manager_approval && !state?.amenity?.require_payment && (
        <Box sx={{ marginTop: 2 }}>
          <Typography variant="h6" color="primary">
            Management Approval Required
          </Typography>
        </Box>
      )}

      {/* Button for holding booking */}
      <Box sx={{ marginTop: 2 }}>
        {isBookingHeld ? (
          <CircularProgress />
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmBooking}
            disabled={timer === 0}
          >
            Confirm Booking
          </Button>
        )}
      </Box>

      <Box sx={{ marginTop: 4 }}>
        <Typography variant="body2" color="textSecondary">
          By booking, you agree to our terms and conditions. Please read the full disclaimer.
        </Typography>
      </Box>
    </Paper>
  );
};

export default BookingConfirmation;
